import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Shop, ShopProduct } from '../services/shopService';
import './AdvancedSearch.css';

interface SearchFilters {
  query: string;
  shopType: string;
  category: string;
  minPrice: number;
  maxPrice: number;
  inStock: boolean;
  rating: number;
  sortBy: 'name' | 'price' | 'rating' | 'newest';
  sortOrder: 'asc' | 'desc';
}

interface SearchResult {
  shop: Shop;
  products: ShopProduct[];
  totalProducts: number;
}

interface AdvancedSearchProps {
  onResults: (results: SearchResult[]) => void;
  onLoading: (loading: boolean) => void;
  onError: (error: string | null) => void;
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  onResults,
  onLoading,
  onError
}) => {
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    shopType: 'all',
    category: 'all',
    minPrice: 0,
    maxPrice: 1000,
    inStock: true,
    rating: 0,
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const [shops, setShops] = useState<Shop[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Load initial data
  useEffect(() => {
    loadShops();
    loadCategories();
  }, []);

  const loadShops = async () => {
    try {
      const shopData = await apiService.getShops();
      setShops(shopData);
    } catch (error) {
      console.error('Error loading shops:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryData = await apiService.getCategoriesForSearch();
      const categoryNames = categoryData.map(cat => cat.name);
      setCategories(categoryNames);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSearch = async () => {
    if (!filters.query.trim()) {
      onResults([]);
      return;
    }

    setIsSearching(true);
    onLoading(true);
    onError(null);

    try {
      const results = await apiService.searchAllShops(filters.query, {
        shopType: filters.shopType !== 'all' ? filters.shopType : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        minPrice: filters.minPrice > 0 ? filters.minPrice : undefined,
        maxPrice: filters.maxPrice < 1000 ? filters.maxPrice : undefined
      });

      // Convert API results to SearchResult format
      const searchResults: SearchResult[] = results.map(result => ({
        shop: result.shop,
        products: result.products.map(product => ({
          ...product,
          shopId: (product as any).shop_id || (product as any).shopId,
          category_id: product.category_id,
          stock: (product as any).stock_quantity || 0,
          isActive: product.is_active !== undefined ? product.is_active : true,
          createdAt: product.created_at || new Date().toISOString(),
          updatedAt: product.updated_at || new Date().toISOString()
        } as ShopProduct)),
        totalProducts: result.products.length
      }));

      // Apply additional filters
      let filteredResults = searchResults;

      if (filters.inStock) {
        filteredResults = filteredResults.map(result => ({
          ...result,
          products: result.products.filter(product => product.inStock)
        }));
      }

      if (filters.rating > 0) {
        filteredResults = filteredResults.map(result => ({
          ...result,
          products: result.products.filter(product => product.rating >= filters.rating)
        }));
      }

      // Sort results
      filteredResults = filteredResults.map(result => ({
        ...result,
        products: sortProducts(result.products, filters.sortBy, filters.sortOrder)
      }));

      onResults(filteredResults);
    } catch (error) {
      console.error('Search error:', error);
      onError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
      onLoading(false);
    }
  };

  const sortProducts = (products: ShopProduct[], sortBy: string, sortOrder: string) => {
    return [...products].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'rating':
          aValue = a.rating;
          bValue = b.rating;
          break;
        case 'newest':
          aValue = new Date(a.createdAt || new Date()).getTime();
          bValue = new Date(b.createdAt || new Date()).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      shopType: 'all',
      category: 'all',
      minPrice: 0,
      maxPrice: 1000,
      inStock: true,
      rating: 0,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  };

  const getShopTypeIcon = (type: string) => {
    switch (type) {
      case 'medical': return '🏥';
      case 'bakery': return '🥖';
      case 'restaurant': return '🍛';
      case 'grocery': return '🛒';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      default: return '🏪';
    }
  };

  return (
    <div className="advanced-search">
      <div className="search-header">
        <h2>🔍 Advanced Search</h2>
        <p>Find exactly what you're looking for across all shops</p>
      </div>

      <div className="search-bar">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Search products, brands, or descriptions..."
            value={filters.query}
            onChange={(e) => handleFilterChange('query', e.target.value)}
            className="search-input"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={isSearching || !filters.query.trim()}
            className="search-button"
          >
            {isSearching ? '🔍 Searching...' : '🔍 Search'}
          </button>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="filter-toggle"
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'} ⚙️
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Shop Type:</label>
              <select
                value={filters.shopType}
                onChange={(e) => handleFilterChange('shopType', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Shop Types</option>
                {Array.from(new Set(shops.map(shop => shop.type))).map(type => (
                  <option key={type} value={type}>
                    {getShopTypeIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Category:</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Price Range:</label>
              <div className="price-range">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice || ''}
                  onChange={(e) => handleFilterChange('minPrice', parseFloat(e.target.value) || 0)}
                  className="price-input"
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice || ''}
                  onChange={(e) => handleFilterChange('maxPrice', parseFloat(e.target.value) || 1000)}
                  className="price-input"
                />
              </div>
            </div>

            <div className="filter-group">
              <label>Sort By:</label>
              <div className="sort-controls">
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="filter-select"
                >
                  <option value="name">Name</option>
                  <option value="price">Price</option>
                  <option value="rating">Rating</option>
                  <option value="newest">Newest</option>
                </select>
                <select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  className="filter-select"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>

            <div className="filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.inStock}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                />
                Only show in-stock items
              </label>
            </div>

            <div className="filter-group">
              <label>Minimum Rating:</label>
              <div className="rating-slider">
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="0.5"
                  value={filters.rating}
                  onChange={(e) => handleFilterChange('rating', parseFloat(e.target.value))}
                  className="rating-range"
                />
                <span className="rating-value">{filters.rating}⭐</span>
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear All Filters
            </button>
            <button onClick={handleSearch} className="apply-filters-btn">
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Quick Search Suggestions */}
      <div className="quick-search">
        <h4>Quick Search:</h4>
        <div className="quick-search-tags">
          {['medical supplies', 'indian food', 'bakery items', 'electronics', 'clothing'].map(tag => (
            <button
              key={tag}
              onClick={() => {
                handleFilterChange('query', tag);
                handleSearch();
              }}
              className="quick-search-tag"
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
